// Decides what CRS every layer in a GIS import batch reprojects INTO:
//  1. The project already has a known CRS -> that's the target, always.
//  2. The project is genuinely EMPTY -> adopt whichever resolved source CRS
//     is most common among the layers being imported (tie -> prefer a
//     LINK-type layer -> first-seen order). That becomes the project's CRS.
//  3. Neither (real elements exist but no CRS was ever assigned) -> BLOCK
//     the import entirely — there's no in-session UI to assign a CRS to an
//     already-open project, and guessing here would just relocate the
//     wrong-coordinate-system bug this feature exists to prevent.

import type { TextKey } from "../../i18n/en.ts";
import { LINK_TARGET_SECTIONS, type ImportTargetSection } from "./importTargets.ts";

interface CrsResolution {
  /** `null` only when import must be blocked entirely (branch 3). */
  targetProj4: string | null;
  /** Set only on branch 2. */
  adoptedProj4?: string;
  /** Set only on branch 3. */
  blockedReason?: TextKey;
}

export function resolveImportTargetCrs(
  currentProjectCrs: string | null,
  projectIsEmpty: boolean,
  layersBeingImported: readonly { sourceCrs: string | null; section: ImportTargetSection }[]
): CrsResolution {
  if (currentProjectCrs != null) {
    return { targetProj4: currentProjectCrs };
  }

  if (projectIsEmpty) {
    const counts = new Map<string, { count: number; isLink: boolean }>();
    for (const { sourceCrs, section } of layersBeingImported) {
      if (sourceCrs == null) continue;
      const entry = counts.get(sourceCrs) ?? { count: 0, isLink: false };
      entry.count++;
      entry.isLink ||= LINK_TARGET_SECTIONS.has(section);
      counts.set(sourceCrs, entry);
    }
    let winner: string | null = null;
    let best: { count: number; isLink: boolean } | null = null;
    for (const [crs, entry] of counts) {
      const better =
        best === null ||
        entry.count > best.count ||
        (entry.count === best.count && entry.isLink && !best.isLink);
      if (better) {
        winner = crs;
        best = entry;
      }
    }
    return winner != null ? { targetProj4: winner, adoptedProj4: winner } : { targetProj4: null };
  }

  return { targetProj4: null, blockedReason: "gisImport.unknownProjectCrsBlocked" };
}
